import type { ReplitDashboardData, ReplitDocument } from '@/types/replit-dashboard';
import type { StartupWhatsNewWindow } from '@/types/startup';
import type { RecordsSectionId } from './RecordsPanel';
import { formatRecordDate, formatRecordTime, parseRecordDate } from './records-utils';

export interface SectionHint {
  key: string;
  params?: Record<string, string>;
  fresh: boolean;
}

type Data = Pick<ReplitDashboardData, 'documents' | 'labResults' | 'imagingStudies' | 'vitalSigns' | 'medications' | 'conditions'> & {
  /**
   * Diagnostic-imaging-report documents. The caller filters `documents` down
   * to clinical notes (excluding imaging reports and procedure notes) before
   * calling this function, and passes the imaging reports separately here so
   * the imaging hint can see them without the notes hint firing on them too.
   */
  imagingReportDocs?: ReplitDocument[];
};

const newestInWindow = (dates: Array<string | undefined>, from: number, to: number): string | undefined => {
  let best: { at: number; iso: string } | undefined;
  for (const d of dates) {
    const parsed = parseRecordDate(d);
    if (!parsed) continue;
    const at = parsed.getTime();
    if (at < from || at > to) continue;
    if (!best || at > best.at) best = { at, iso: d as string };
  }
  return best?.iso;
};

/**
 * Computed from record timestamps only — no model output. `window` is the
 * startup payload's whatsNewWindow; without one, the last 24 hours.
 */
export function sectionHints(
  data: Data,
  window: StartupWhatsNewWindow | undefined,
  language: string,
  now: Date = new Date(),
): Partial<Record<RecordsSectionId, SectionHint>> {
  const to = now.getTime();
  const sinceVisit = window?.kind === 'sinceVisit' ? parseRecordDate(window.since)?.getTime() : undefined;
  const from = sinceVisit ?? to - 24 * 60 * 60 * 1000;
  const time = (iso: string) => ({ time: formatRecordTime(iso, language) });
  const hints: Partial<Record<RecordsSectionId, SectionHint>> = {};

  const note = newestInWindow((data.documents || []).map((d) => d.createdAt), from, to);
  if (note) hints.notes = { key: 'records.hintNewNote', params: time(note), fresh: true };

  const lab = newestInWindow((data.labResults || []).map((l) => l.date), from, to);
  if (lab) hints.labs = { key: 'records.hintNewResults', params: time(lab), fresh: true };

  const study = newestInWindow(
    [...(data.imagingStudies || []).map((s) => s.date), ...(data.imagingReportDocs || []).map((d) => d.createdAt)],
    from,
    to,
  );
  if (study) hints.imaging = { key: 'records.hintNewStudy', params: time(study), fresh: true };

  const vital = newestInWindow((data.vitalSigns || []).map((v) => v.recordedAt), from, to);
  if (vital) hints.vitals = { key: 'records.hintUpdated', params: time(vital), fresh: true };

  const med = newestInWindow((data.medications || []).map((m) => m.prescribedDate), from, to);
  hints.medications = med
    ? { key: 'records.hintChanged', params: time(med), fresh: true }
    : { key: sinceVisit ? 'records.hintNoChangesSinceVisit' : 'records.hintNoChangesToday', fresh: false };

  const onset = newestInWindow((data.conditions || []).map((c) => c.onsetDate), from, to);
  if (onset) hints.conditions = { key: 'records.hintNew', params: { date: formatRecordDate(onset, language) }, fresh: true };

  return hints;
}
